import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

const TONES = {
  purple: 'bg-purple-50 text-[#7C3AED] hover:bg-purple-100',
  blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
  green: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100',
  whatsapp: 'bg-green-50 text-green-600 hover:bg-green-100',
  amber: 'bg-amber-50 text-amber-600 hover:bg-amber-100',
  red: 'bg-red-50 text-red-500 hover:bg-red-100',
  gray: 'bg-gray-50 text-gray-600 hover:bg-gray-100',
  off: 'bg-gray-50 text-gray-300 cursor-not-allowed',
};

const ROW =
  'flex items-center gap-2 h-9 px-3 rounded-lg text-xs font-semibold transition whitespace-nowrap w-full disabled:opacity-40 disabled:cursor-not-allowed';

export default function ActionMenu({ phone, hideContact = false, items = [], extra = null }) {
  const [pos, setPos] = useState(null); // null = closed
  const menuRef = useRef(null);

  const digits = String(phone || '').replace(/\D/g, '');
  const full = digits.length === 10 ? `91${digits}` : digits;

  const close = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('actions-menu-open', close);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('actions-menu-open', close);
    };
  }, [pos]);

  // measure the popup, then flip it upward if it does not fit below
  useLayoutEffect(() => {
    if (!pos || pos.ready || !menuRef.current) return;
    const h = menuRef.current.offsetHeight;
    let top = pos.top;
    if (top + h > window.innerHeight - 8) top = Math.max(8, pos.btnTop - h - 6);
    setPos({ ...pos, top, ready: true });
  }, [pos]);

  const toggle = (e) => {
    if (pos) { close(); return; }
    window.dispatchEvent(new Event('actions-menu-open')); // closes any other open menu
    const r = e.currentTarget.getBoundingClientRect();
    setPos({
      top: r.bottom + 6,
      btnTop: r.top,
      left: Math.max(8, Math.min(r.right - 208, window.innerWidth - 216)),
      ready: false,
    });
  };

  const list = items.filter(Boolean);
  const normal = list.filter((i) => !i.danger);
  const danger = list.filter((i) => i.danger);

  const renderItem = (it) => {
    const cls = `${ROW} ${TONES[it.tone || 'gray']}`;
    const content = (
      <>
        <span className="w-4 text-center">{it.icon}</span>
        {it.label}
      </>
    );
    if (it.to) {
      return <Link key={it.label} to={it.to} onClick={close} className={cls}>{content}</Link>;
    }
    return (
      <button
        key={it.label}
        type="button"
        disabled={it.disabled}
        onClick={() => { close(); it.onClick && it.onClick(); }}
        className={cls}
      >
        {content}
      </button>
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold text-white whitespace-nowrap bg-gradient-to-r from-[#7C3AED] to-[#EC4899] shadow-sm hover:opacity-90"
      >
        Actions <span className="text-[10px]">&#9662;</span>
      </button>

      {pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[100]" onClick={close} />
            <div
              ref={menuRef}
              style={{ top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden' }}
              className="fixed z-[101] w-52 bg-white rounded-2xl shadow-2xl ring-1 ring-purple-100 p-2 max-h-[80vh] overflow-y-auto"
            >
              <p className="px-2 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Quick actions
              </p>

              <div className="flex flex-col gap-1.5">
                {!hideContact && (
                  <div className="grid grid-cols-2 gap-1.5">
                    {digits ? (
                      <a href={`tel:+${full}`} onClick={close} className={`${ROW} ${TONES.green} justify-center`}>
                        &#128222; Call
                      </a>
                    ) : (
                      <span className={`${ROW} ${TONES.off} justify-center`}>&#128222; Call</span>
                    )}
                    {digits ? (
                      <a
                        href={`https://wa.me/${full}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={close}
                        className={`${ROW} ${TONES.whatsapp} justify-center`}
                      >
                        &#128172; Chat
                      </a>
                    ) : (
                      <span className={`${ROW} ${TONES.off} justify-center`}>&#128172; Chat</span>
                    )}
                  </div>
                )}

                {normal.map(renderItem)}

                {extra && (
                  <div
                    className="flex flex-col gap-1.5 [&>button]:w-full [&>button]:justify-start [&>button]:h-9 [&>button]:rounded-lg [&>button]:shadow-none [&>select]:w-full [&>select]:h-9 [&>select]:rounded-lg"
                    onClick={(e) => { if (e.target.closest('button')) close(); }}
                    onChange={close}
                  >
                    {extra}
                  </div>
                )}

                {danger.length > 0 && <div className="border-t border-gray-100 my-0.5" />}
                {danger.map(renderItem)}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}